// js
import React, { useEffect, useRef, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import layout from "./dictionaries/layout.js";
import { useSpring, animated } from '@react-spring/three';
import * as THREE from 'three';

import Board from "./components/Board.jsx";
import PiecesSection from "./components/PiecesSection.jsx";
import Team from "./components/Team.jsx";
import GameCamera from "./sceneSetUp/GameCamera.jsx";
import DisconnectModal from "./components/DisconnectModal.jsx";

// three js
// import { Leva, useControls } from "leva"
import { Perf } from 'r3f-perf'

// server
import { getSocket } from "./socket.js";

const socket = getSocket();
import { useParams } from "wouter";
import { 
  deviceAtom, 
  readyToStartAtom, 
  languageAtom,
  hostAtom, 
  gamePhaseAtom, 
  turnAtom,
  legalTilesAtom,
  tilesAtom,
  helperTilesAtom,
  winnerAtom,
  clientAtom,
  joinTeamAtom,
  yootAnimationAtom,
  teamsAtom,
  hasTurnAtom,
  settingsOpenAtom,
  mainMenuOpenAtom,
  pauseGameAtom,
  timerAtom,
  turnExpireTimeAtom,
  backdoLaunchAtom,
  nakAtom,
  yutMoCatchAtom,
  backToMainMenuOpenAtom,
  editGuestsOpenAtom,
  editOneGuestOpenAtom,
  guestBeingEdittedAtom,
  showFinishMovesAtom,
  showBonusAtom,
  musicAtom,
  logDisplayAtom,
  showGameRulebookAtom,
  isOnlineAtom,
  languageModalOpenAtom,
  throwCountAtom,
  selectionAtom,
  shortcutOptionsAtom,
  audioVolumeAtom,
  minigameGamePhaseAtom,
  minigameRuleAtom,
  minigameStateAtom,
} from "./GlobalState.jsx";
import MoveList from "./components/MoveList.jsx";
import PiecesOnBoard from "./components/PiecesOnBoard.jsx";
import ScoreButtons from "./components/ScoreButtons.jsx";
import { Center, Float, Text3D, useGLTF } from "@react-three/drei";
import { Color, MeshStandardMaterial } from "three";
import { useFrame } from "@react-three/fiber";
import GameLog from "./components/GameLog.jsx";

// react spring
import YootNew from "./components/YootNew.jsx";
import YootButtonNew from "./components/YootButtonNew.jsx";
import useResponsiveSetting from "./hooks/useResponsiveSetting.jsx";
import MeteorsRealShader from "./shader/meteorsReal/MeteorsRealShader.jsx";
import PauseGame from "./components/PauseGame.jsx";
import { useAnimationPlaying } from "./hooks/useAnimationPlaying.jsx";
import Settings from "./components/Settings.jsx";
import YutBonus from "./components/YutBonus.jsx";
import Chatbox from "./components/Chatbox.jsx";
import AudioButton from "./soundPlayers/AudioButton.jsx";
import MilkyWayNew from "./shader/milkyway/MilkyWayNew.jsx";
import RocketsWin2 from "./endScenes/RocketsWin2.jsx";
import RocketsLose from "./endScenes/RocketsLose.jsx";
import UfosWin2New from "./endScenes/UfosWin2New.jsx";
import UfosLose from "./endScenes/UfosLose.jsx";
import LanguageButtonGame from "./components/LanguageButtonGame.jsx";
import LanguageModalAnimated from "./components/lobby/LanguageModalAnimated.jsx";
import BackToMainMenu from "./components/lobby/BackToMainMenu.jsx";
import translations from "./dictionaries/translations.js";
import MeshColors from "./components/MeshColors.jsx";
import { getLegalTiles } from "./logicHelpers/legalTiles.jsx";
import { useGameHelpers } from "./stores/useGameHelpers.js";
import { firstAvailableTokenId } from "./gameLogic/helpers.js";
import useSoundEffectsPlayer from "./soundPlayers/useSoundEffectsPlayer.jsx";
import MinigameScene2 from "./minigames/MinigameScene2.jsx";
import DodgeMeteorsScene from "./minigames/DodgeMeteorsScene.jsx";
import BubbleMatchScene from "./minigames/BubbleMatchScene.jsx";
import HideAndSeekScene from "./minigames/HideAndSeekScene.jsx";
import { IS_DEV } from "./config/env.js";
import { trackEvent } from './api';

// There should be no state
export default function Game() {
  
  useResponsiveSetting();
  const device = useAtomValue(deviceAtom)
  const gamePhase = useAtomValue(gamePhaseAtom)
  const minigameGamePhase = useAtomValue(minigameGamePhaseAtom)
  const minigameRule = useAtomValue(minigameRuleAtom)
  const minigameState = useAtomValue(minigameStateAtom)
  const pauseGame = useAtomValue(pauseGameAtom)
  const logDisplay = useAtomValue(logDisplayAtom)
  const winner = useAtomValue(winnerAtom)
  const client = useAtomValue(clientAtom)
  const host = useAtomValue(hostAtom)
  const setShowRulebook = useSetAtom(showGameRulebookAtom)
  const isOnline = useAtomValue(isOnlineAtom)
  const language = useAtomValue(languageAtom)
  const backToMainMenuOpen = useAtomValue(backToMainMenuOpenAtom)
  // test
  // const client = { team: 0 }
  // const gamePhase = 'finished'
  // const winner = 1

  useEffect(() => {
    setShowRulebook(false)
  }, [])

  useEffect(() => {
    if (gamePhase === 'pregame' || gamePhase === 'game') {
      trackEvent({ surface: 'game', action: 'phase_view', category: 'page_view', metadata: { phase: gamePhase } })
    } else if (gamePhase === 'finished' && client.socketId === host?.socketId) {
      trackEvent({ surface: 'end_scene', action: 'phase_view', category: 'page_view' })
    } else if (gamePhase === 'minigame') {
      trackEvent({ surface: 'minigame', action: 'phase_view', category: 'page_view', metadata: { type: minigameState.type ?? 'stars' } })
    }
  }, [client.socketId, gamePhase, host?.socketId, minigameState.type])

  // Animations
  const { gameScale } = useSpring({
    gameScale: (gamePhase === 'pregame' || gamePhase === 'game') ? device === 'landscapeDesktop' ? 1.1 : 1 : 0,
    config: {
      tension: 170,
      friction: 26
    },
  })

  function SettingsButton({ position, scale }) {
    const [open, setOpen] = useAtom(settingsOpenAtom)
    const setMainMenuOpen = useSetAtom(mainMenuOpenAtom)
    const setEditGuestsOpen = useSetAtom(editGuestsOpenAtom)
    const setEditOneGuestOpen = useSetAtom(editOneGuestOpenAtom)
    const setGuestBeingEditted = useSetAtom(guestBeingEdittedAtom)
    const setLanguageModalOpen = useSetAtom(languageModalOpenAtom)
    const [hover, setHover] = useState(false)
    function handlePointerEnter(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
      }
    }
    function handlePointerMove(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
      }
    }
    function handlePointerLeave(e) {
      e.stopPropagation();
      if (hover) {
        setHover(false)
      }
    }

    function handlePointerDown(e) {
      e.stopPropagation();
      if (open) {
        setOpen(false)
        if (hover) {
          setHover(false)
        }
      } else {
        setOpen(true)
        setMainMenuOpen(true)
        setEditGuestsOpen(false)
        setEditOneGuestOpen(false)
        setGuestBeingEditted(null)
        setLanguageModalOpen(false)
        setShowRulebook(false)
      }
      trackEvent({ surface: 'game', action: open ? 'close_settings' : 'open_settings', category: 'ui' })
    }

    return <group position={position} scale={scale}>
      <mesh>
        <meshStandardMaterial color={hover? 'green' : 'yellow'}/>
        <boxGeometry args={[2.1, 0.03, 0.55]}/>
      </mesh>
      <mesh>
        <boxGeometry args={[2.05, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
        onPointerMove={e => handlePointerMove(e)}
      >
        <boxGeometry args={[2.1, 0.04, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={layout[device].game.settings.mainButton.text.position}
          rotation={layout[device].game.settings.mainButton.text.rotation}
          size={layout[device].game.settings.mainButton.text.size}
          height={layout[device].game.settings.mainButton.text.height}
        >
          <meshStandardMaterial color={hover? 'green' : 'yellow'}/>
          {translations.game.settings[language]}
        </Text3D>
      </Center>
      {open && <Settings
        position={[-4.7, 6, 6.3]}
        scale={[1, 1, 1]}
      />}
    </group>
  }

  function DisplayHostAndSpectating() {
    const host = useAtomValue(hostAtom)
    const client = useAtomValue(clientAtom)

    const text = () => {
      if (host && client.team === -1 && client.socketId === host.socketId) {
        return `${translations.game.viewer[language]}, ${translations.game.host[language]}`
      } else if (client.team === -1) {
        return translations.game.viewer[language]
      } else if (host && client.team !== -1 && client.socketId === host.socketId) {
        return translations.game.host[language]
      } else {
        return ''
      }
    }    

    return <Center position={layout[device].game.spectatingAndHosting.line0Pos} key={Math.random()} left={device==='landscapeDesktop'} right={device==='portrait'}>
      <Text3D 
        name='spectating-text'
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        rotation={layout[device].game.spectatingAndHosting.rotation}
        size={layout[device].game.spectatingAndHosting.size}
        height={layout[device].game.spectatingAndHosting.height}
      >
        {text()}
        <meshStandardMaterial color='yellow'/>
      </Text3D>
    </Center>
  }

  function GameBoard() {
    const gamePhase = useAtomValue(gamePhaseAtom)
    const tiles = useAtomValue(tilesAtom)
    const legalTiles = useAtomValue(legalTilesAtom)
    const helperTiles = useAtomValue(helperTilesAtom)
    const showFinishMoves = useAtomValue(showFinishMovesAtom)
    const showBonus = useAtomValue(showBonusAtom)
    const animationPlaying = useAnimationPlaying()

    const { boardScale, boardPosition } = useSpring({
      boardScale: layout[device].game.board[gamePhase].scale,
      boardPosition: layout[device].game.board[gamePhase].position,
    })

    return <animated.group position={boardPosition} scale={boardScale}>
      <Board 
        position={[0,0,0]}
        scale={1}
        tiles={tiles}
        legalTiles={legalTiles}
        helperTiles={helperTiles}
        interactive={true}
        showStart={true}
        device={device}
        showFinishMoves={showFinishMoves}
        showBonus={showBonus}
        animationPlaying={animationPlaying}
        starColor={MeshColors.menuGold}
      />
    </animated.group>
  }
  function WhoGoesFirstText() {
    return <group>
      <Text3D
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={layout[device].game.whoGoesFirst.title.position}
      rotation={layout[device].game.whoGoesFirst.title.rotation}
      size={layout[device].game.whoGoesFirst.title.size}
      height={layout[device].game.whoGoesFirst.title.height}
      >
        {translations.game.whoGoesFirst.title[language]}
        <meshStandardMaterial color="limegreen"/>
      </Text3D>
      <Text3D
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={layout[device].game.whoGoesFirst.description.position}
      rotation={layout[device].game.whoGoesFirst.description.rotation}
      size={layout[device].game.whoGoesFirst.description.size}
      height={layout[device].game.whoGoesFirst.title.height}
      lineHeight={layout[device].game.whoGoesFirst.title.lineHeight}
      >
        {translations.game.whoGoesFirst.description[language]}
        <meshStandardMaterial color="limegreen"/>
      </Text3D>
    </group>
  }
  const yutAnimation = useAtomValue(yootAnimationAtom)

  // game log button
  // chat button
  // chat component
  function ChatButton({ position, rotation, scale }) {
  const [hover, setHover] = useState(false)
  const setLogDisplay = useSetAtom(logDisplayAtom)
  function handlePointerEnter(e) {
    e.stopPropagation();
    if (!hover) {
      setHover(true)
      document.body.style.cursor = "pointer";
    }
  }
  function handlePointerMove(e) {
    e.stopPropagation();
    if (!hover) {
      setHover(true)
      document.body.style.cursor = "pointer";
    }
  }
  function handlePointerLeave(e) {
    e.stopPropagation();
    if (hover) {
      setHover(false)
      document.body.style.cursor = "default";
    }
  }
  function handlePointerDown(e) {
    e.stopPropagation();
    if (logDisplay !== 'chat') {
      setLogDisplay('chat')
    }
  }

  return <group position={position} rotation={rotation} scale={scale}>
    <mesh>
      <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
      {/* <meshStandardMaterial color={(hover || logDisplay === 'chat') ? 'green' : 'yellow'}/> */}
      <boxGeometry args={layout[device].game.chat.button.outerBox.args}/>
    </mesh>
    <mesh>
      <boxGeometry args={layout[device].game.chat.button.innerBox.args}/>
      <meshStandardMaterial color='black'/>
    </mesh>
    <mesh 
      name='wrapper' 
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}
      onPointerMove={e => handlePointerMove(e)}
    >
      <boxGeometry args={[
        layout[device].game.chat.button.outerBox.args[0],
        layout[device].game.chat.button.innerBox.args[1],
        layout[device].game.chat.button.outerBox.args[2]
      ]}/>
      <meshStandardMaterial transparent opacity={0}/>
    </mesh>
    <Text3D
      font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
      position={layout[device].game.chat.button.text.position}
      rotation={layout[device].game.chat.button.text.rotation}
      size={layout[device].game.chat.button.text.size}
      height={layout[device].game.chat.button.text.height}
    >
      <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
      {/* <meshStandardMaterial color={(hover || logDisplay === 'chat') ? 'green' : 'yellow'}/> */}
      {translations.game.chat[language]}
    </Text3D>
  </group>
  }

  function GameLogsButton({ position, rotation, scale }) {
    const [hover, setHover] = useState(false)
    const setLogDisplay = useSetAtom(logDisplayAtom)
    function handlePointerEnter(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
        document.body.style.cursor = "pointer";
      }
    }
    function handlePointerMove(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
        document.body.style.cursor = "pointer";
      }
    }
    function handlePointerLeave(e) {
      e.stopPropagation();
      if (hover) {
        setHover(false)
        document.body.style.cursor = "default";
      }
    }
    function handlePointerDown(e) {
      e.stopPropagation();
      if (logDisplay !== 'logs') {
        setLogDisplay('logs')
      }
    }

    return <group position={position} rotation={rotation} scale={scale}>
      <mesh>
        <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
        {/* <meshStandardMaterial color={(hover || logDisplay === 'logs') ? 'green' : 'yellow'}/> */}
        <boxGeometry args={layout[device].game.chat.button.outerBox.args}/>
      </mesh>
      <mesh>
        <boxGeometry args={layout[device].game.chat.button.innerBox.args}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
        onPointerMove={e => handlePointerMove(e)}
      >
        <boxGeometry args={[
          layout[device].game.chat.button.outerBox.args[0],
          layout[device].game.chat.button.innerBox.args[1],
          layout[device].game.chat.button.outerBox.args[2]
        ]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={layout[device].game.chat.button.text.position}
        rotation={layout[device].game.chat.button.text.rotation}
        size={layout[device].game.chat.button.text.size}
        height={layout[device].game.chat.button.text.height}
      >
        <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
        {/* <meshStandardMaterial color={(hover || logDisplay === 'logs') ? 'green' : 'yellow'}/> */}
        {translations.game.logs[language]}
      </Text3D>
    </group>
  }

  function MobileControls() {
    // if throws > 0, show throw button
    // else, if you have a move, show mobile control buttons
    // else, it's not your turn
    // if you threw, and animation is playing,
    // display command buttons in grey 
    // when animation ends, enable it
    const throws = useAtomValue(throwCountAtom)
    const [selection, setSelection] = useAtom(selectionAtom)
    const teams = useAtomValue(teamsAtom)
    const team = client.team
    const backdoLaunch = useAtomValue(backdoLaunchAtom)
    const shortcutOptions = useAtomValue(shortcutOptionsAtom)
    const setLegalTiles = useSetAtom(legalTilesAtom)
    const setTokenChoices = useGameHelpers(s => s.setTokenChoices)
    const setTokenChoice = useGameHelpers(s => s.setTokenChoice)
    const nextTokenChoice = useGameHelpers(s => s.nextTokenChoice)
    const prevTokenChoice = useGameHelpers(s => s.prevTokenChoice)
    const tokenChoice = useGameHelpers(s => s.tokenChoice)
    const tokenChoices = useGameHelpers(s => s.tokenChoices)
    const tiles = useAtomValue(tilesAtom)
    const params = useParams()
    const animationPlaying = useAnimationPlaying()
    const playSoundEffect = useSoundEffectsPlayer().playSoundEffect
    const hasTurn = useAtomValue(hasTurnAtom)
    const audioVolume = useAtomValue(audioVolumeAtom)
    const [showFinishMoves, setShowFinishMoves] = useAtom(showFinishMovesAtom)
    const legalTiles = useAtomValue(legalTilesAtom)



    function handleGoPointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = "pointer";
    }
    function handleGoPointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = "default";
    }
    // first step: ship
    // second step: tile
    function handleGoClick(e) {
      e.stopPropagation();
      if (selection === null) {
        const tile = tokenChoices[tokenChoice]
        let pieces, history
        if (tile === -1) {
          history = []
          pieces = [{tile, team, id: firstAvailableTokenId({ pieces: teams[team].pieces }), history}]
        } else {
          history = tiles[tile][0].history // go back the way you came from of the first token
          pieces = tiles[tile]
        }

        // for visual aid
        const legalTiles = getLegalTiles(tile, teams[team].moves, teams[team].pieces, history, backdoLaunch, shortcutOptions)
        setSelection({ tile, pieces })
        setLegalTiles(legalTiles)

        // for mobile control buttons
        // turn tile indexes in legalTiles into a set
        const legalTilesSet = new Set()
        for (const key in legalTiles) {
          legalTilesSet.add(parseInt(key))
        }
        // turn it into an array to iterate through them
        const legalTilesArray = Array.from(legalTilesSet)
        setTokenChoices(legalTilesArray)
        setTokenChoice(0)

        socket.emit("select", { 
          roomId: params.id.toUpperCase(), 
          selection: { tile, pieces }, 
          legalTiles 
        })


      } else {
        // check whether show-finish-moves is true
        if (tokenChoices[tokenChoice] === 29 && legalTiles[29]) {
          if (legalTiles[29].length > 1) {
            setShowFinishMoves(true)
          } else {
            socket.emit('score', { roomId: params.id.toUpperCase(), selectedMove: legalTiles[29][0], playerName: client.name });
          }
        } else {
          // clear legal tiles

          const selectedTile = tokenChoices[tokenChoice]
          socket.emit("move", { 
            roomId: params.id.toUpperCase(), 
            tile: selectedTile, 
            playerName: client.name 
          });
          
          setShowFinishMoves(false)
          
          playSoundEffect('/sounds/effects/star-move.mp3', audioVolume)
        }
      }
    }

    function handleLeftArrowPointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = "pointer";
      // change color
    }

    function handleLeftArrowPointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = "default";
      // change color
    }
    
    function handleLeftArrowPointerDown(e) {
      e.stopPropagation();
      prevTokenChoice();
    }
    
    function handleRightArrowPointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = "pointer";
      // change color
    }

    function handleRightArrowPointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = "default";
      // change color
    }
    function handleRightArrowPointerDown(e) {
      e.stopPropagation();
      nextTokenChoice();
    }

    function FlashingCommandControls({ children }) {
      const timer = useAtomValue(timerAtom)
      const paused = useAtomValue(pauseGameAtom)
      const turnExpireTime = useAtomValue(turnExpireTimeAtom)
      const controlsGroup = useRef()
      const materials = useRef([])

      useEffect(() => {
        if (!controlsGroup.current) return undefined
        const clonedMaterials = []
        controlsGroup.current.traverse((object) => {
          if (!object.isMesh || object.name.includes('wrapper') || !object.material) return
          const sourceMaterials = Array.isArray(object.material) ? object.material : [object.material]
          const nextMaterials = sourceMaterials.map((sourceMaterial) => {
            const color = sourceMaterial.color
            const luminance = typeof color?.r === 'number' && typeof color?.g === 'number' && typeof color?.b === 'number'
              ? color.r * 0.2126 + color.g * 0.7152 + color.b * 0.0722
              : 1
            if (luminance <= 0.08) {
              return sourceMaterial
            }
            const material = sourceMaterial.clone()
            material.transparent = true
            material.depthWrite = false
            clonedMaterials.push(material)
            return material
          })
          object.material = Array.isArray(object.material) ? nextMaterials : nextMaterials[0]
        })
        materials.current = clonedMaterials
        return () => clonedMaterials.forEach((material) => material.dispose())
      }, [])

      useFrame((state) => {
        const secondsRemaining = Math.max(0, (turnExpireTime - Date.now()) / 1000)
        const opacity = !timer || paused || !turnExpireTime || secondsRemaining > 10
          ? 1
          : secondsRemaining > 5
          ? 0.1 + (secondsRemaining - 5) / 5 * 0.9
          : 0.5 + Math.sin(state.clock.elapsedTime * Math.PI * 2) * 0.5
        for (const material of materials.current) {
          material.opacity = opacity
        }
      })

      return <group ref={controlsGroup}>{children}</group>
    }

    function FinishMovesButtonSet({ position, scale }) {
      
      const legalTiles = useAtomValue(legalTilesAtom)
      const moves = legalTiles[29]

      const tokenPositions = [
        [0, 0, 0],
        [1.2, 0, 0],
        [0, 0, 1.2],
        [1.2, 0, 1.2],
        [2.4, 0, 0],
        [2.4, 0, 1.2],
      ]
      return <group position={position} scale={scale}>
        <group name='launch-button' position={layout[device].game.commandControls.finishMovesButtonSet.position}>
          {/* replace with icon */}
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            position={[-1.8, 0.1, legalTiles[29] && legalTiles[29].length > 2 ? -2.6 : -2.2]}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.7}
            height={0.01}
            lineHeight={0.7}
          >
            {`SCORE\nWITH`}
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </Text3D>
          {/* move array */}
          <group 
            position={[-1.2, 0, -0.4]}
            scale={1.5}
          >
          { moves && moves.map((value, index) => ( // must use parentheses instead of brackets
            <MoveToken
              moveInfo={value} 
              position={tokenPositions[index]}
              key={index}
            />
          ))}
          </group>
        </group>
      </group>
    }

    function MoveToken({moveInfo, position}) {
  
      const [hover, setHover] = useState(false);
      const paused = useAtomValue(pauseGameAtom)
      const primaryMaterial = new MeshStandardMaterial({ color: 'limegreen' })
      // console.log(primaryMaterial.color.r) // 0.03190
      // console.log(primaryMaterial.color.g) // 0.6105
  
      useFrame((state) => {
        const time = state.clock.elapsedTime;
        if (!hover) {
          // primaryMaterial.color.g = 0.3105 + Math.cos(time * 5) * 0.3 + 0.1
          primaryMaterial.color.setHSL(Math.cos(time * 5) * 0.25 + 0.3, Math.cos(time * 5) * 0.06 + 1, Math.cos(time * 5) * 0.06 + 0.3);
        } else {
          primaryMaterial.color.r = 0.7
          primaryMaterial.color.g = 1
          primaryMaterial.color.b = 1
        }
      })
  
      function scorePointerEnter(event) {
        event.stopPropagation();
        if (hasTurn) {
          document.body.style.cursor = "pointer";
          setHover(true)
        }
      }
    
      function scorePointerOut(event) {
        event.stopPropagation();
        if (hasTurn) {
          document.body.style.cursor = "default";
          setHover(false)
        }
      }
  
      return <group position={position}>
        <mesh rotation={[0, 0, 0]} material={primaryMaterial}>
          <cylinderGeometry args={[0.5, 0.5, 0.1]}/>
        </mesh>
        <mesh rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.45, 0.45, 0.11]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
        <Text3D 
        font="/fonts/Luckiest Guy_Regular.json" 
        height={0.01} 
        size={0.5} 
        position={[-0.17, 0.05, 0.23]}
        rotation={[-Math.PI/2, 0, 0]}
        material={primaryMaterial}>
          {`${moveInfo.move}`}
        </Text3D>
        <mesh 
          name='wrapper' 
          position={[0,0,0]} 
          rotation={[0, 0, 0]}
          onPointerEnter={scorePointerEnter}
          onPointerLeave={scorePointerOut}
          onPointerDown={() => {
            if (hasTurn && !paused) {
              socket.emit('score', { roomId: params.id.toUpperCase(), selectedMove: moveInfo, playerName: client.name });
              setHover(false)
            }
          }}
        >
          <cylinderGeometry args={[0.5, 0.5, 0.15]}/>
          <meshStandardMaterial transparent opacity={0}/>
        </mesh>
      </group> 
    }

    function CommandControls({ position, scale }) {
      return <group position={position} scale={scale}>
        <group name='prev-button' position={[-0.1, 0, 0]} scale={0.7}>
          <mesh name='prev-button-outer' scale={[2, 0.01, 2]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </mesh>
          <mesh name='prev-button-inner' scale={[1.8, 0.02, 1.8]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark}/>
          </mesh>
          <mesh name='prev-button-wrapper' scale={[2, 0.02, 2]}
            onPointerEnter={ e => handleLeftArrowPointerEnter(e)}
            onPointerLeave={ e => handleLeftArrowPointerLeave(e)}
            onPointerDown={e => handleLeftArrowPointerDown(e)}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark} transparent opacity={0}/>
          </mesh>
          <mesh name='prev-button-arrow' position={[0.2, 0, 0]} rotation={[0, Math.PI/6, 0]} scale={[0.7, 0.1, 0.7]} >
            <cylinderGeometry args={[1, 1, 1, 3]}/>
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </mesh>
        </group>
        <group name='next-button' position={[1.6, 0, 0]} scale={0.7}>
          <mesh name='next-button-outer' scale={[2, 0.01, 2]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </mesh>
          <mesh name='next-button-inner' scale={[1.8, 0.02, 1.8]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark}/>
          </mesh>
          <mesh name='next-button-wrapper' scale={[2, 0.02, 2]}
            onPointerEnter={ e => handleRightArrowPointerEnter(e)}
            onPointerLeave={ e => handleRightArrowPointerLeave(e)}
            onPointerDown={e => handleRightArrowPointerDown(e)}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark} transparent opacity={0}/>
          </mesh>
          <mesh name='next-button-arrow' rotation={[0, -Math.PI/6, 0]} scale={[0.7, 0.1, 0.7]} position={[-0.2, 0, 0]} >
            <cylinderGeometry args={[1, 1, 1, 3]}/>
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </mesh>
        </group>
        <group name='launch-button' position={[0.75, 0, 2.0]}>
          <mesh name='launch-button-outer' scale={[3.1, 0.01, 2]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </mesh>
          <mesh name='launch-button-inner' scale={[2.9, 0.02, 1.8]}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark}/>
          </mesh>
          <mesh name='launch-button-wrapper' scale={[3, 0.02, 2]}
            onPointerEnter={e => handleGoPointerEnter(e)}
            onPointerLeave={e => handleGoPointerLeave(e)}
            onPointerDown={e => handleGoClick(e)}>
            <boxGeometry args={[1, 1, 1, 32]}/>
            <meshStandardMaterial color={MeshColors.spaceDark} transparent opacity={0}/>
          </mesh>
          {/* replace with icon */}
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            position={[-0.8, 0.1, 0.4]}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.8}
            height={0.01}
          >
            GO!
            <meshStandardMaterial color={MeshColors.menuGold}/>
          </Text3D>
        </group>
      </group>
    }

    // case 1: show throw button
    // case 2: no throw available: show mobile control buttons
    // case 3: no throw available and finish tile clicked with multiple moves: show finish moves
    return <>
      { (gamePhase === 'pregame' || gamePhase === 'game') && throws > 0 && !animationPlaying && hasTurn && minigameGamePhase !== 'pending' && <YootButtonNew
        position={layout[device].game.yootButton.position}
        rotation={layout[device].game.yootButton.rotation}
        scale={layout[device].game.yootButton.scale}
      /> }
      { (gamePhase === 'pregame' || gamePhase === 'game') && throws === 0 && !animationPlaying && hasTurn && !showFinishMoves && <FlashingCommandControls>
        <CommandControls 
          position={layout[device].game.commandControls.position}
          scale={layout[device].game.commandControls.scale}
        />
      </FlashingCommandControls> }
      { (gamePhase === 'pregame' || gamePhase === 'game') && throws === 0 && !animationPlaying && hasTurn && showFinishMoves && <FlashingCommandControls>
        <FinishMovesButtonSet 
          position={layout[device].game.commandControls.position}
          scale={layout[device].game.commandControls.scale}
        />
      </FlashingCommandControls> }
    </>
  }

  function MinigameButton() {
    const [hover, setHover] = useState(false)
    const [showSelector, setShowSelector] = useState(false)
    const params = useParams()

    function handlePointerEnter(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
      }
      document.body.style.cursor = "pointer";
    }

    function handlePointerMove(e) {
      e.stopPropagation();
      if (!hover) {
        setHover(true)
      }
      document.body.style.cursor = "pointer";
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      if (hover) {
        setHover(false)
      }
      document.body.style.cursor = "default";
    }

    function handlePointerDown(e) {
      e.stopPropagation();
      setShowSelector(true)
      trackEvent({ surface: 'game', action: 'open_minigame_selector', category: 'ui' })
    }

    function launchMinigame(event, initializer) {
      event.stopPropagation()
      if (params.id) socket.emit(initializer, { roomId: params.id.toUpperCase() })
      setShowSelector(false)
      trackEvent({ surface: 'minigame', action: 'start', category: 'gameplay', metadata: { type: initializer } })
    }

    function RideButton({ label, position, initializer, onClick }) {
      const [rideHover, setRideHover] = useState(false)
      return <group position={position}>
        <mesh><boxGeometry args={[8.2, 0.05, 0.9]} /><meshStandardMaterial color={rideHover ? 'limegreen' : 'yellow'} /></mesh>
        <mesh
          position={[0, 0.04, 0]}
          onPointerEnter={(event) => { event.stopPropagation(); setRideHover(true); document.body.style.cursor = 'pointer' }}
          onPointerLeave={(event) => { event.stopPropagation(); setRideHover(false); document.body.style.cursor = 'default' }}
          onPointerDown={(event) => onClick ? onClick(event) : launchMinigame(event, initializer)}
        ><boxGeometry args={[8.2, 0.1, 0.9]} /><meshStandardMaterial transparent opacity={0} /></mesh>
        <Center position={[0, 0.07, 0]}><Text3D font="/fonts/Luckiest Guy_Regular.json" size={0.29} height={0.01} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color='black' />{label}</Text3D></Center>
      </group>
    }

    return <group position={layout[device].game.minigameButton.position} scale={layout[device].game.minigameButton.scale} rotation={layout[device].game.minigameButton.rotation}>
      {!showSelector && <>
        <mesh>
          <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
          <boxGeometry args={[1.6, 0.03, 0.7]}/>
        </mesh>
        <mesh>
          <boxGeometry args={[1.55, 0.04, 0.65]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
        <mesh
          name='wrapper'
          onPointerEnter={e => handlePointerEnter(e)}
          onPointerLeave={e => handlePointerLeave(e)}
          onPointerDown={e => handlePointerDown(e)}
          onPointerMove={e => handlePointerMove(e)}
        >
          <boxGeometry args={[2.1, 0.04, 0.55]}/>
          <meshStandardMaterial transparent opacity={0}/>
        </mesh>
        <Center position={[0, 0.05, 0]}>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            size={0.2}
            height={0.01}
            rotation={[-Math.PI/2, 0, 0]}
          >
            <meshStandardMaterial color={hover ? 'green' : 'yellow'}/>
            {translations.gameSettings.minigame[language]}
          </Text3D>
        </Center>
      </>}
      {showSelector && <group position={[-2, 0.08, -3.2]}>
        <RideButton label='HIDE AND SEEK' position={[0, 0, -2.3]} initializer='minigameInitializeHideAndSeek' />
        <RideButton label='COLLECT STARS' position={[0, 0, -1.15]} initializer='minigameInitializeStars' />
        <RideButton label='DODGE METEORS' position={[0, 0, 0]} initializer='minigameInitializeDodgeMeteors' />
        <RideButton label='MATCH 3' position={[0, 0, 1.15]} initializer='minigameInitializeBubbleMatch' />
        <RideButton label='CLOSE' position={[0, 0, 2.3]} onClick={(event) => { event.stopPropagation(); setShowSelector(false); document.body.style.cursor = 'default' }} />
      </group>}
    </group>
  }

  const meteorShaderColor = new THREE.Color();
  meteorShaderColor.setHSL(0.05, 0.7, 0.4)
  return (<>
      {/* <Perf/> */}
      {/* <Leva hidden /> */}
      <GameCamera position={layout[device].camera.position} fitVertically={gamePhase === 'minigame'}/>
      { (gamePhase === 'pregame' || gamePhase === 'game') && <animated.group scale={gameScale}>
        <Team 
          position={layout[device].game.team0.position}
          scale={layout[device].game.team0.scale}
          team={0} 
        />
        <Team 
          position={layout[device].game.team1.position}
          scale={layout[device].game.team1.scale}
          team={1} 
        />
        { socket.connected && (gamePhase === 'pregame' || gamePhase === 'game') && logDisplay === 'chat' && <Chatbox
          device={device}
          disabled={backToMainMenuOpen}
          boxHeight={layout[device].game.chat.box.height}
          boxWidth={layout[device].game.chat.box.width}
          padding={layout[device].game.chat.box.padding}
          fontSize={layout[device].game.chat.box.fontSize}
          borderRadius={layout[device].game.chat.box.borderRadius}
          position={layout[device].game.chat.position} 
          rotation={layout[device].game.chat.rotation}
          scale={layout[device].game.chat.scale}
        /> }
        { IS_DEV && minigameRule && (gamePhase === 'pregame' || gamePhase === 'game') && <MinigameButton/> }
        { (!socket.connected || !isOnline) && <DisconnectModal
          position={layout[device].game.disconnectModal.position}
          rotation={layout[device].game.disconnectModal.rotation}
        /> }
        { pauseGame && <PauseGame position={[0, 5, 2]}/> }
        <GameBoard/>
        { gamePhase === "pregame" && <WhoGoesFirstText/> }
        <MobileControls/>
        { yutAnimation && <YootNew 
        scale={layout[device].game.yootNew.scale} 
        position={layout[device].game.yootNew.position} 
        animation={yutAnimation}/> }
        <YutBonus position={layout[device].game.yutBonus.position} scale={layout[device].game.yutBonus.scale}/>
        <SettingsButton 
          position={layout[device].game.settings.mainButton.position}
          scale={layout[device].game.settings.mainButton.scale}
        />
        <AudioButton 
          position={layout[device].game.audioButton.position}
          scale={layout[device].game.audioButton.scale}
        />
        <LanguageButtonGame
          position={layout[device].game.languageButton.position}
          rotation={layout[device].game.languageButton.rotation}
          scale={layout[device].game.languageButton.scale}
        />
        <LanguageModalAnimated
          position={layout[device].game.languageModal?.position ?? layout[device].lobby.languageModal.position}
          scale={layout[device].game.languageModal?.scale ?? layout[device].lobby.languageModal.scale}
          animate={false}
        />
        <DisplayHostAndSpectating/>
        <PiecesSection 
          position={layout[device].game.piecesSection.position}
          device={device}
        />
        { gamePhase === 'game' && <PiecesOnBoard boardOffset={layout[device].game.board['game'].position[2]}/> }
        <MoveList
          position={layout[device].game.moveList.position}
          rotation={layout[device].game.moveList.rotation}
          tokenScale={layout[device].game.moveList.tokenScale}
          tokenPosition={layout[device].game.moveList.tokenPosition}
          size={layout[device].game.moveList.size}
          piecePosition={layout[device].game.moveList.piecePosition}
          pieceScale={layout[device].game.moveList.pieceScale}
          gamePhase={gamePhase}
        />
      </animated.group> }
      { (gamePhase === 'pregame' || gamePhase === 'game') && <group name='background'>
        <MeteorsRealShader color={meteorShaderColor}/>
        <MilkyWayNew // will not show without a camera
          rotation={[-Math.PI/2, 0, -35.0]} 
          position={[0, -10, -4]}
          scale={5}
          brightness={0.5}
          colorTint1={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
          colorTint2={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
          colorTint3={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
        />
      </group> }
      { gamePhase === 'minigame' && (minigameState.type === 'hideAndSeek' ? <HideAndSeekScene/> : minigameState.type === 'dodgeMeteors' ? <DodgeMeteorsScene/> : minigameState.type === 'bubbleMatch' ? <BubbleMatchScene/> : <MinigameScene2/>)}
      { gamePhase === 'finished' && client.team === 0 && winner === 0 && <RocketsWin2/> }
      { gamePhase === 'finished' && client.team === 0 && winner === 1 && <RocketsLose/> }
      { gamePhase === 'finished' && client.team === 1 && winner === 1 && <UfosWin2New/> }
      { gamePhase === 'finished' && client.team === 1 && winner === 0 && <UfosLose/> }
      { gamePhase === 'finished' && client.team === -1 && winner === 0 && <RocketsWin2/> }
      { gamePhase === 'finished' && client.team === -1 && winner === 1 && <UfosWin2New/> }
      <BackToMainMenu
        confirmationModalPosition={layout[device].lobby.confirmationModal.position}
        confirmationModalScale={layout[device].lobby.confirmationModal.scale}
        roomClosedModalPosition={layout[device].lobby.roomClosedModal.position}
        roomClosedModalScale={layout[device].lobby.roomClosedModal.scale}
      />
    </>
  );
}